/* ===========================================================================
   Attribution — storefront half
   ---------------------------------------------------------------------------
   Partner and campaign links point straight at the shop:

     /?affid=cashkaro&aff_sub1=CHKR123&utm_source=cashkaro&utm_medium=affiliate

   `affid` names the partner who earns the commission. `aff_sub1`…`aff_sub5`
   are the partner's OWN ids (their click or transaction reference), kept
   verbatim and sent back to them in the postback — that is how a cashback
   site credits the shopper who clicked. UTMs are captured on every visit,
   partner or not, for campaign reporting.

   Two independent memories, because they answer different questions:
     partner touch  — who gets paid. Only another partner link replaces it; a
                      later Instagram or Google visit does not take a partner's
                      commission away.
     campaign touch — which campaign last brought the shopper. Any UTM link
                      replaces it.

   Both are written to CART ATTRIBUTES, which survive Shopify's checkout (a
   different origin that cannot read anything stored here) and come back on
   the order as note_attributes.

   Everything is best-effort. A shopper must never lose a purchase because
   attribution failed, so every step is wrapped and failure is silent.
   ========================================================================= */
(function () {
  "use strict";

  var AFF_KEY = "ddc_aff";
  var UTM_KEY = "ddc_utm";
  var LEGACY_KEY = "ddc_attr";
  var WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  var SUBS = ["sub1", "sub2", "sub3", "sub4", "sub5"];
  var UTMS = ["source", "medium", "campaign", "content", "term"];
  var MAX = 200;

  /* Ad-platform click ids. gclid/gbraid/wbraid = Google Ads, msclkid =
     Microsoft Ads, ttclid = TikTok, li_fat_id = LinkedIn. fbclid is kept too,
     but on its own it does not mean "paid": Meta adds it to organic post clicks
     as well as ads. These are left in the URL — ad and analytics tags read them. */
  var AD_PARAMS = ["gclid", "gbraid", "wbraid", "msclkid", "ttclid", "fbclid", "li_fat_id"];

  /* Click-id parameters that affiliate NETWORKS append to the landing URL
     themselves. Their names are fixed by each network, not by us:
       irclid Impact · admitad_uid Admitad · cjevent CJ · clickref Partnerize
       awc Awin
     Seeing one of these marks a partner visit even when the network's
     destination URL was set up without our own `affid`. */
  // Not the generic clickid / click_id / transaction_id: ad and email tools add
  // those to ordinary links, and a visit carrying one replaced the real partner
  // on the cart (QA, 2026-09-14). Kept in step with lib/attribution.js.
  var NETWORK_PARAMS = ["irclid", "admitad_uid", "cjevent", "clickref", "awc"];
  var MAX_PARAMS = 600;

  /* Storage throws outright in some contexts (Safari private mode, embedded
     webviews, blocked cookies) rather than returning null. */
  function load(k) {
    try { return JSON.parse(localStorage.getItem(k) || "null"); } catch (e) { return null; }
  }
  function save(k, v) {
    try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* ignore */ }
  }
  function fresh(rec) {
    return rec && rec.at && Date.now() - rec.at < WINDOW_MS ? rec : null;
  }

  var params;
  try { params = new URL(location.href).searchParams; } catch (e) { return; }
  function p(name) { return (params.get(name) || "").trim().slice(0, MAX); }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* --- visitor and session -----------------------------------------------
     A random id for this browser, holding no name, email or phone. It links
     visits into a journey, and rides on the cart so the order can find it. */
  var vid = load("ddc_vid");
  if (typeof vid !== "string" || !/^[0-9a-f-]{36}$/.test(vid)) {
    vid = uuid();
    save("ddc_vid", vid);
  }
  var newSession = false;
  try {
    newSession = !sessionStorage.getItem("ddc_sess");
    sessionStorage.setItem("ddc_sess", "1");
  } catch (e) { newSession = false; }

  /* --- capture ---------------------------------------------------------- */

  var now = Date.now();
  var affid = p("affid").toLowerCase() || p("ddc_ref").toLowerCase(); // ddc_ref: links issued before affid
  if (affid && !/^[a-z0-9][a-z0-9_-]{0,39}$/.test(affid)) affid = "";

  var netParam = "";
  NETWORK_PARAMS.some(function (k) { if (p(k)) { netParam = k; return true; } return false; });

  /* The partner's whole query string, minus our own and UTM keys, so the
     server can pick out whichever click-id parameter that partner is set up
     to use — including networks this list has never heard of. Only captured
     on a partner visit, never on an ordinary page view. */
  function partnerParams() {
    var out = [];
    params.forEach(function (v, k) {
      var key = k.toLowerCase();
      if (/^(utm_|aff_sub\d|affid$|ddc_)/.test(key)) return;
      out.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(v).slice(0, MAX)));
    });
    return out.join("&").slice(0, MAX_PARAMS);
  }

  var partner = fresh(load(AFF_KEY));
  var campaign = fresh(load(UTM_KEY));
  var landedClick = null;

  if (affid || netParam) {
    var subs = {};
    SUBS.forEach(function (k, i) { subs[k] = p("aff_sub" + (i + 1)); });
    partner = { affid: affid, subs: subs, params: partnerParams(), click: uuid(), at: now };
    save(AFF_KEY, partner);
    landedClick = partner.click;
  } else if (!partner) {
    /* Carry forward a partner touch recorded by the previous version of this
       script, so a shopper mid-journey when this shipped keeps their credit. */
    var legacy = fresh(load(LEGACY_KEY));
    if (legacy && legacy.ref) {
      partner = { affid: String(legacy.ref), subs: {}, click: legacy.cid || "", at: legacy.at };
      save(AFF_KEY, partner);
    }
  }

  var utm = {};
  var hasUtm = false;
  UTMS.forEach(function (k) {
    utm[k] = p("utm_" + k);
    if (utm[k]) hasUtm = true;
  });
  if (hasUtm) {
    campaign = { utm: utm, at: now };
    save(UTM_KEY, campaign);
  }

  /* --- keep partner ids out of shared links ----------------------------
     Strip affid and the sub ids from the address bar once captured. A shopper
     who shares the page would otherwise hand the partner's click id — which
     identifies THEM to the cashback site — to whoever they share it with, and
     the friend's purchase would credit the original shopper's cashback.
     UTMs are left alone: analytics tools read them from the URL too. */
  if (affid || netParam || p("ddc_ref") || p("ddc_cid")) {
    try {
      var u = new URL(location.href);
      ["affid", "aff_sub1", "aff_sub2", "aff_sub3", "aff_sub4", "aff_sub5", "ddc_ref", "ddc_cid"]
        .concat(NETWORK_PARAMS)
        .forEach(function (k) { u.searchParams.delete(k); });
      history.replaceState(history.state, "", u.pathname + u.search + u.hash);
    } catch (e) { /* ignore */ }
  }

  /* --- record the visit -------------------------------------------------
     One touch per browsing session, plus one for any arrival carrying a
     partner, campaign or ad tag (clicking an ad mid-session is a new touch).
     Every order's journey is assembled from these, through the App Proxy on
     the shop's own domain. fetch with keepalive survives an instant navigation;
     sendBeacon cannot send the JSON content type the proxy insists on. */
  var ad = {};
  var hasAd = false;
  AD_PARAMS.forEach(function (k) { var v = p(k); if (v) { ad[k] = v; hasAd = true; } });

  var referrer = "";
  try {
    if (document.referrer) {
      var rh = new URL(document.referrer).hostname;
      // Only the hostname ever leaves the page: a full referring URL can carry
      // a search query or an email address in its query string.
      if (rh && rh !== location.hostname) referrer = rh;
    }
  } catch (e) { referrer = ""; }

  var tagged = Boolean(affid || netParam || hasUtm || hasAd);
  if (newSession || tagged) {
    try {
      fetch("/apps/ddc/touch", {
        method: "POST",
        keepalive: true,
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vid: vid,
          touchId: landedClick || uuid(),
          newSession: newSession,
          affid: affid || "",
          subs: (affid || netParam) ? partner.subs : {},
          params: (affid || netParam) ? partner.params : "",
          utm: utm,
          ad: ad,
          referrer: referrer,
          path: location.pathname,
        }),
      }).catch(function () {});
    } catch (e) { /* ignore */ }
  }

  /* --- write to the cart ------------------------------------------------
     Compared against the LIVE cart on every page view rather than tracked
     with a "synced" flag. After a purchase Shopify starts a brand-new empty
     cart, and a flag set on the old one would leave a second order inside the
     30-day window with no attribution at all. */

  function sync() {
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var cur = cart.attributes || {};
        // Always present, so every order can be joined to its journey.
        var want = { ddc_vid: vid };

        if (partner) {
          want.ddc_affid = partner.affid || "";
          want.ddc_params = partner.params || "";
          want.ddc_click = partner.click || "";
          /* When the partner click happened, so the server can apply THAT
             partner's cookie window (Admitad programmes run 10 or 30 days)
             instead of this script's upper bound. */
          want.ddc_at = String(partner.at || "");
          /* Every sub slot is written, empty ones as "". A new partner link
             must clear sub ids left by the previous partner, or their click id
             would be echoed back as if it belonged to this sale. */
          SUBS.forEach(function (k, i) { want["ddc_sub" + (i + 1)] = partner.subs[k] || ""; });
          if (cur.ddc_ref) want.ddc_ref = "";   // retire attributes from the
          if (cur.ddc_cid) want.ddc_cid = "";   // previous link format
        }
        if (campaign) {
          UTMS.forEach(function (k) { want["ddc_utm_" + k] = campaign.utm[k] || ""; });
        }

        var diff = {};
        var changed = false;
        Object.keys(want).forEach(function (k) {
          if ((cur[k] || "") !== want[k]) { diff[k] = want[k]; changed = true; }
        });
        if (!changed) return;

        /* /cart/update.js with `attributes` changes only the keys given; an
           empty string removes a key. Line items are untouched. */
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ attributes: diff }),
        });
      })
      .catch(function () { /* next page view tries again */ });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();
